/**
 * Speech-bubble rendering — D11.
 *
 * Reads `model.speech_bubbles[]` (spawned by the `Spoke` event applier),
 * positions a screen-space rounded rectangle + text over the speaker's
 * on-screen avatar, and fades alpha linearly to zero over 5 seconds.
 * Multiple concurrent bubbles for the same speaker stack vertically
 * (newest at the bottom, closest to the head).
 *
 * Policy per CLIENT_DESIGN §11.3 / §12.3:
 *   - Zone-chat only. `/world` broadcasts do NOT bubble — this renderer
 *     only receives `Spoke`-driven bubbles, which are already zone-scoped
 *     by the gateway.
 *   - Max 3 concurrent per speaker — enforced in the applier.
 *   - Plain text, no markdown.
 *   - 5-second total lifespan: 4 s full-opacity + 1 s linear fade.
 *
 * The renderer lives on the screen-space `overlayLayer` (attached to
 * `app.stage`, not the camera-transformed `worldContainer`), so bubbles
 * stay pinned to the avatar even as the camera pans/zooms — we
 * recompute each bubble's screen position every frame via `worldToScreen`.
 */

import { Container, Graphics, Text } from 'pixi.js';

import type { PlayerEntry, SpeechBubble, WorldModel } from '../world/model';
import { worldToScreen, type CameraState } from './iso';
import type { SceneLayers } from './scene';

const BUBBLE_FULL_MS = 4_000;
const BUBBLE_FADE_MS = 1_000;
const BUBBLE_LIFETIME_MS = BUBBLE_FULL_MS + BUBBLE_FADE_MS;
const BUBBLE_MAX_WIDTH_PX = 220;
const BUBBLE_PADDING_X = 8;
const BUBBLE_PADDING_Y = 4;
const BUBBLE_SPACING_Y = 4;
const AVATAR_HEAD_ABOVE_FLOOR_PX = 64; // approx head offset from tile centre

export interface BubbleRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    nowMs: number,
  ) => void;
  destroy: () => void;
}

interface BubbleNode {
  container: Container;
  bg: Graphics;
  text: Text;
  startedAtMs: number;
}

export function createBubbleRenderer(layers: SceneLayers): BubbleRenderer {
  const root = new Container({ label: 'bubbles-root' });
  root.eventMode = 'none';
  layers.overlayLayer.addChild(root);

  const pool = new Map<number, BubbleNode>();

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    nowMs: number,
  ): void => {
    const activeIds = new Set<number>();

    // Group bubbles by speaker so we can stack them vertically.
    const bySpeaker = new Map<number, SpeechBubble[]>();
    for (const bubble of model.speech_bubbles) {
      const player = asPlayerId(bubble.speaker);
      if (player === null) continue; // Phase 1 — SOMA bubbles deferred to Phase 2+
      const list = bySpeaker.get(player);
      if (list) list.push(bubble);
      else bySpeaker.set(player, [bubble]);
    }

    for (const [playerId, bubbles] of bySpeaker) {
      const speakerPlayer = model.players.get(playerId);
      if (!speakerPlayer) continue;

      // Oldest first, newest last; newest is closest to the head so we
      // render bottom-up.
      const ordered = bubbles;
      // Track each bubble's lifetime to compute fade + cull expired.
      const live: Array<{ bubble: SpeechBubble; alpha: number }> = [];
      for (const b of ordered) {
        const existing = pool.get(b.id);
        const startedAt = existing?.startedAtMs ?? nowMs;
        const age = nowMs - startedAt;
        if (age >= BUBBLE_LIFETIME_MS) continue;
        const alpha =
          age <= BUBBLE_FULL_MS
            ? 1
            : 1 - (age - BUBBLE_FULL_MS) / BUBBLE_FADE_MS;
        live.push({ bubble: b, alpha });
      }

      // Stack live bubbles upward from the speaker's head. Newest at the
      // bottom of the stack (closest to head).
      const headScreen = headPositionScreen(
        speakerPlayer,
        cam,
        viewportW,
        viewportH,
      );

      let cursorY = headScreen.y - 10;
      for (let i = live.length - 1; i >= 0; i--) {
        const { bubble, alpha } = live[i]!;
        const node = ensureBubbleNode(
          pool,
          bubble,
          root,
          nowMs,
          activeIds,
        );
        activeIds.add(bubble.id);

        // Lay out text + background once per bubble (not per frame) by
        // remembering the last text we rendered. Cheap if the string is
        // unchanged.
        if (node.text.text !== bubble.utterance) {
          node.text.text = bubble.utterance;
          layoutBubble(node);
        }

        const w = node.bg.width;
        const h = node.bg.height;
        node.container.position.set(
          Math.round(headScreen.x - w / 2),
          Math.round(cursorY - h),
        );
        node.container.alpha = alpha;
        cursorY = cursorY - h - BUBBLE_SPACING_Y;
      }
    }

    // Destroy pool entries that no longer correspond to live bubbles.
    for (const [id, node] of pool) {
      if (activeIds.has(id)) continue;
      node.container.destroy({ children: true });
      pool.delete(id);
    }
  };

  return {
    render,
    destroy: () => {
      for (const node of pool.values()) {
        node.container.destroy({ children: true });
      }
      pool.clear();
      root.destroy();
    },
  };
}

function asPlayerId(e: SpeechBubble['speaker']): number | null {
  return 'Player' in e ? e.Player : null;
}

/** Compute the screen position ~ over the avatar's head at the given
 *  world-space player entry, under the current camera. */
function headPositionScreen(
  player: PlayerEntry,
  cam: CameraState,
  viewportW: number,
  viewportH: number,
): { x: number; y: number } {
  const [tx, ty] = player.position;
  const floor = worldToScreen(tx, ty, cam, viewportW, viewportH);
  return { x: floor.x, y: floor.y - AVATAR_HEAD_ABOVE_FLOOR_PX * cam.zoom };
}

function ensureBubbleNode(
  pool: Map<number, BubbleNode>,
  bubble: SpeechBubble,
  root: Container,
  nowMs: number,
  _activeIds: Set<number>,
): BubbleNode {
  const existing = pool.get(bubble.id);
  if (existing) return existing;
  const container = new Container({ label: `bubble-${bubble.id}` });
  const bg = new Graphics();
  const text = new Text({
    text: bubble.utterance,
    style: {
      fill: 0x111217,
      fontSize: 12,
      fontFamily: 'sans-serif',
      wordWrap: true,
      wordWrapWidth: BUBBLE_MAX_WIDTH_PX - BUBBLE_PADDING_X * 2,
    },
  });
  text.position.set(BUBBLE_PADDING_X, BUBBLE_PADDING_Y);
  container.addChild(bg, text);
  root.addChild(container);
  const node: BubbleNode = {
    container,
    bg,
    text,
    startedAtMs: nowMs,
  };
  layoutBubble(node);
  pool.set(bubble.id, node);
  return node;
}

/** Redraw the rounded-rect background to fit the current text metrics. */
function layoutBubble(node: BubbleNode): void {
  const w = Math.ceil(node.text.width + BUBBLE_PADDING_X * 2);
  const h = Math.ceil(node.text.height + BUBBLE_PADDING_Y * 2);
  node.bg.clear();
  node.bg.roundRect(0, 0, w, h, 6);
  node.bg.fill({ color: 0xf4f1ea, alpha: 0.95 });
  node.bg.stroke({ color: 0x333333, width: 1, alpha: 0.8 });
}
